import React from 'react';
import ErrorBoundary from './ErrorBoundary';
import useAsyncData from '../hooks/useAsyncData';
import useStaleWarning from '../hooks/useStaleWarning';
import useAutoRefresh from '../hooks/useAutoRefresh';

const ActiveSessions = () => {
  const { data: sessionsPayload, loading, error, retry } = useAsyncData('/api/sessions');
  const { isStale, lastUpdated } = useStaleWarning(sessionsPayload);
  useAutoRefresh(retry);

  const sessions = sessionsPayload?.sessions || [];
  const fmtTime = (d) => d ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '–';

  if (loading) {
    return (
      <ErrorBoundary>
        <article className="panel sessions-panel" aria-busy="true">
          <h2>Active Sessions</h2>
          <div className="skeleton">
            <div className="skeleton-line" />
            <div className="skeleton-line" />
            <div className="skeleton-line" />
          </div>
        </article>
      </ErrorBoundary>
    );
  }

  if (error) {
    return (
      <ErrorBoundary>
        <article className="panel sessions-panel" aria-live="assertive">
          <h2>Active Sessions</h2>
          <p className="error-msg">Failed to load sessions.</p>
          <button type="button" className="btn-retry" onClick={retry}>Retry</button>
        </article>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <article className="panel sessions-panel" aria-label="Active sessions" aria-busy="false">
        <div className="panel-header">
          <h2>Active Sessions</h2>
          <div className="panel-meta">
            <span className="last-updated" aria-label="Last updated">
              Last updated: {fmtTime(lastUpdated)}
            </span>
            {isStale && (
              <span className="badge badge-stale" role="status">Stale</span>
            )}
            <button type="button" className="btn-icon btn-refresh" onClick={retry} aria-label="Refresh sessions">
              ↻
            </button>
          </div>
        </div>
        <ul className="session-list" aria-label="Active sessions">
          {sessions.length === 0 && <li className="empty">No active sessions</li>}
          {sessions.map((s, i) => (
            <li key={s.sessionKey || i} className="session-item">
              <strong>{s.label || s.sessionKey || 'Session ' + i}</strong>
              <span>{s.status || 'active'}</span>
              {s.title && <span className="session-title">{s.title}</span>}
            </li>
          ))}
        </ul>
      </article>
    </ErrorBoundary>
  );
};

export default ActiveSessions;
